// The generic receipt every payment type prints (tuition today; transport, library, exam later).
// Each type maps its own data to this shape - see feeReceiptMapper.ts - and ReceiptDocument draws it.

export interface ReceiptLineItem {
  description: string; // e.g. "Tuition Fee", "Half-yearly Examination Fee"
  terms?: string; // e.g. "1, 2, 3"; the Terms column shows only when a row has terms
  due: number;
  concession: number;
  paid: number;
}

export interface ReceiptModel {
  receiptNo: number;
  receiptDate: string;
  student: {
    name: string;
    admissionNo: string;
    className: string; // "Nine - A"
    academicYear?: string;
  };
  items: ReceiptLineItem[];
  payment: {
    mode: string;
    transactionId?: string; // shown for non-cash payments
    date?: string;
    balance?: { label: string; amount: number }; // e.g. "Term 3 Balance"
  };
  totalPaid: number;
  signedBy?: { label: string; name: string }; // "Received By" / "Paid online by"
}

// The school's details at the top of every receipt (Settings → School Profile)
export interface SchoolHeader {
  name: string;
  address: string;
  contact?: string; // "9876543210 · office@school.in"
  logo?: string; // data URL (PNG/JPG); without one the "LOGO" placeholder is printed
}

// Until the School Profile is saved
export const PLACEHOLDER_SCHOOL: SchoolHeader = {
  name: "School Name",
  address: "School address line 1, Area, City, State - 000000",
  contact: "Phone · Email",
};

export const rupees = (value: number) => `₹${Number(value ?? 0).toLocaleString("en-IN")}`;

const ONES = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve",
  "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

const belowHundred = (n: number) => (n < 20 ? ONES[n] : `${TENS[Math.floor(n / 10)]} ${ONES[n % 10]}`.trim());
const belowThousand = (n: number) =>
  n >= 100 ? `${ONES[Math.floor(n / 100)]} Hundred ${belowHundred(n % 100)}`.trim() : belowHundred(n);

// Indian numbering: 1,23,45,678 -> One Crore Twenty Three Lakh Forty Five Thousand Six Hundred Seventy Eight
export const amountInWords = (value: number): string => {
  let n = Math.floor(Math.abs(value ?? 0));
  if (n === 0) return "Zero Rupees Only";
  const parts: string[] = [];
  const crore = Math.floor(n / 10000000);
  n %= 10000000;
  const lakh = Math.floor(n / 100000);
  n %= 100000;
  const thousand = Math.floor(n / 1000);
  n %= 1000;
  if (crore) parts.push(`${belowThousand(crore)} Crore`);
  if (lakh) parts.push(`${belowHundred(lakh)} Lakh`);
  if (thousand) parts.push(`${belowHundred(thousand)} Thousand`);
  if (n) parts.push(belowThousand(n));
  return `${parts.join(" ")} Rupees Only`;
};
